import React, {useEffect, useState} from 'react';
import {View, Image, ImageResizeMode, ImageSourcePropType, ImageStyle} from 'react-native';
import * as axios from 'axios';
import {encode} from 'base64-arraybuffer';
import {SizedMap} from '../utils/SizedMap';
import {ComponentProps} from './ComponentProps';

interface ImageViewProps extends ComponentProps {
  url?: string;
  cacheKey?: string;
  source?: ImageSourcePropType;
  resizeMode?: ImageResizeMode;
  imageStyle?: ImageStyle;
}

export interface ImageViewCircularProps extends ImageViewProps {
  size?: number;
}

export const ImageViewCircular = (props: ImageViewCircularProps) => {
  return <ImageViewSquare {...{...props, isCircular: true}} />;
};

export interface ImageViewSquareProps extends ImageViewProps {
  isCircular?: boolean;
  size?: number;
}

export const ImageViewSquare = (props: ImageViewSquareProps) => {
  if (props.cacheKey) {
    return <ImageCached {...{...props}} />;
  } else {
    return <ImageView {...{...props}} />;
  }
};

const ImageView = (props: ImageViewSquareProps) => {
  const size = props.size ?? 50;
  return (
    <View style={props.containerStyle}>
      <Image
        testID={props.testID}
        source={props.source ?? {uri: props.url}}
        resizeMode={props.resizeMode ?? undefined}
        style={[
          props.imageStyle,
          {
            width: size,
            height: size,
          },
        ]}
        borderRadius={props.isCircular ?? false ? size / 2 : undefined}
      />
    </View>
  );
};

export class ImageCachedStore {
  public cache = new SizedMap<string, string>(1000);
  static shared = new ImageCachedStore();
}

const ImageCached = (props: ImageViewProps) => {
  const [imageData, setImageData] = useState<string | null>(null);
  const [imageFallbackURL, setImageFallbackURL] = useState<string | null>();
  useEffect(() => {
    (async () => {
      if (props.cacheKey) {
        const cachedImage = ImageCachedStore.shared.cache.get(props.cacheKey);
        if (cachedImage) {
          setImageData(cachedImage);
          return;
        }
        if (props.url) {
          try {
            const response = await axios.default.get(props.url, {
              responseType: 'arraybuffer',
            });
            const base64 = encode(response.data);
            setImageData(base64);
            ImageCachedStore.shared.cache.set(props.cacheKey, base64);
          } catch (e) {
            setImageFallbackURL(props.url);
          }
        }
      }
    })();
  }, []);
  let source: ImageSourcePropType;
  if (imageFallbackURL) {
    source = {uri: imageFallbackURL};
  } else {
    source = {uri: `data:image/jpeg;base64,${imageData}`};
  }
  return <ImageView {...{...props}} source={source} />;
};
